/**
 * WIDGET SAAS MVP - COMPONENTE REACT
 *
 * Widget flutuante para captura e qualificação de leads SaaS.
 *
 * Features:
 * - Conversa natural com IA Claude
 * - Captura progressiva: conversa → cadastro (nome + contato)
 * - Formulário explícito para coleta de dados
 * - Responsivo (mobile + desktop)
 * - Notificação instantânea por email
 *
 * Fluxo:
 * 1. Usuário abre widget → API /start (session criada)
 * 2. Conversa com IA → API /message (respostas contextualizadas)
 * 3. Clica "Receber informações" → Formulário aparece
 * 4. Preenche nome + contato → API /finalize (lead criado + email enviado)
 *
 * @author Claude Code
 * @date 2025-11-05
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';

// ==========================================
// TYPES
// ==========================================
interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
}

interface WidgetSaaSProps {
  position?: 'bottom-right' | 'bottom-left';
  apiBaseUrl?: string;
}

// API Response Types
interface StartResponse {
  success: boolean;
  session_token?: string;
  greeting?: string;
  error?: string;
}

interface MessageResponse {
  success: boolean;
  response?: string;
  stage?: string;
  lead_created?: boolean; // Indica se lead foi criado automaticamente
  error?: string;
}

interface FinalizeResponse {
  success: boolean;
  lead_id?: string;
  notified?: boolean;
  error?: string;
}

// ==========================================
// WIDGET COMPONENT
// ==========================================
export default function WidgetSaaS({
  position = 'bottom-right',
  apiBaseUrl = '/api/widget',
}: WidgetSaaSProps) {
  // ========== STATE ==========
  const [isOpen, setIsOpen] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Status da conexão com a IA
  const [aiStatus, setAiStatus] = useState<'checking' | 'online' | 'offline'>('checking');

  // Formulário de cadastro
  const [showForm, setShowForm] = useState(false);
  const [nome, setNome] = useState('');
  const [contato, setContato] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Formulário de informações adicionais
  const [showExtraInfoForm, setShowExtraInfoForm] = useState(false);
  const [extraInfo, setExtraInfo] = useState('');
  const [isSendingExtraInfo, setIsSendingExtraInfo] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // ========== COMPUTED ==========
  const positionClasses = {
    'bottom-right': 'bottom-6 right-4 sm:bottom-8 sm:right-6',
    'bottom-left': 'bottom-6 left-4 sm:bottom-8 sm:left-6',
  };

  // ========== API CALLS ==========

  // Health check da API para verificar se IA está online
  const checkAiStatus = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

      const response = await fetch(`${apiBaseUrl}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json() as StartResponse;
        if (data.success) {
          setAiStatus('online');
          return;
        }
      }
      setAiStatus('offline');
    } catch {
      setAiStatus('offline');
    }
  }, [apiBaseUrl]);

  // Verificar status da IA ao montar componente e a cada 60s
  useEffect(() => {
    checkAiStatus();
    const interval = setInterval(checkAiStatus, 60000);
    return () => clearInterval(interval);
  }, [checkAiStatus]);

  // Iniciar conversa
  const startConversation = useCallback(async () => {
    try {
      console.log('[WidgetSaaS] Iniciando conversa...');
      setIsLoading(true);

      const response = await fetch(`${apiBaseUrl}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = (await response.json()) as StartResponse;

      if (data.success) {
        setSessionToken(data.session_token);
        setMessages([
          {
            id: `msg-${Date.now()}`,
            content: data.greeting,
            role: 'assistant',
          },
        ]);
        console.log('[WidgetSaaS] Conversa iniciada com sucesso');
      } else {
        throw new Error(data.error || 'Failed to start conversation');
      }
    } catch (error: any) {
      console.error('[WidgetSaaS] Start error:', error);
      setMessages([
        {
          id: `msg-${Date.now()}`,
          content:
            'Desculpe, ocorreu um erro ao iniciar a conversa. Tente novamente em alguns instantes.',
          role: 'assistant',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  // Enviar mensagem
  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim() || !sessionToken || isLoading) return;

      // Adicionar mensagem do usuário imediatamente (UI optimista)
      const userMsg: Message = {
        id: `msg-${Date.now()}`,
        content: message,
        role: 'user',
      };
      setMessages(prev => [...prev, userMsg]);
      setInputMessage('');
      setIsLoading(true);

      try {
        console.log('[WidgetSaaS] Enviando mensagem...');

        // Cancelar request anterior se existir
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Criar novo AbortController para este request
        abortControllerRef.current = new AbortController();

        const response = await fetch(`${apiBaseUrl}/message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_token: sessionToken,
            message,
          }),
          signal: abortControllerRef.current.signal,
        });

        const data = (await response.json()) as MessageResponse;

        if (data.success && data.response) {
          // Adicionar resposta da IA
          const aiMsg: Message = {
            id: `msg-${Date.now() + 1}`,
            content: data.response,
            role: 'assistant',
          };
          setMessages(prev => [...prev, aiMsg]);
          console.log('[WidgetSaaS] Resposta recebida');

          // Detectar se lead foi criado automaticamente
          if (data.lead_created) {
            console.log('[WidgetSaaS] Lead criado automaticamente pelo backend!');
            setSubmitSuccess(true);
          }
        } else {
          throw new Error(data.error || 'Failed to send message');
        }
      } catch (error: any) {
        // Ignorar erros de abort (esperados quando cancelamos request)
        if (error.name === 'AbortError') {
          console.log('[WidgetSaaS] Request aborted (esperado)');
          return;
        }

        console.error('[WidgetSaaS] Send error:', error);

        // Adicionar mensagem de erro
        setMessages(prev => [
          ...prev,
          {
            id: `msg-${Date.now() + 1}`,
            content: 'Desculpe, ocorreu um erro. Pode tentar novamente?',
            role: 'assistant',
          },
        ]);
      } finally {
        setIsLoading(false);
        // CORREÇÃO CRÍTICA: Re-focar input após envio
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    },
    [sessionToken, isLoading, apiBaseUrl]
  );

  // Finalizar conversa (cadastrar lead)
  const handleFinalize = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!nome.trim() || !contato.trim() || !sessionToken || isSubmitting) return;

      setIsSubmitting(true);

      try {
        console.log('[WidgetSaaS] Finalizando conversa...');

        const response = await fetch(`${apiBaseUrl}/finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_token: sessionToken,
            nome,
            contato,
          }),
        });

        const data = (await response.json()) as FinalizeResponse;

        if (data.success) {
          setSubmitSuccess(true);
          setShowForm(false);

          // Mensagem de confirmação
          setMessages(prev => [
            ...prev,
            {
              id: `msg-${Date.now()}`,
              content: `Perfeito, ${nome}! 🎉\n\nVou te enviar todas as informações no ${contato.includes('@') ? 'email' : 'WhatsApp'} nos próximos minutos.\n\nEnquanto isso, pode continuar navegando pelo site!`,
              role: 'assistant',
            },
          ]);

          console.log('[WidgetSaaS] Lead criado:', data.lead_id);
        } else {
          throw new Error(data.error || 'Failed to finalize');
        }
      } catch (error: any) {
        console.error('[WidgetSaaS] Finalize error:', error);
        alert('Erro ao enviar dados. Tente novamente.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [nome, contato, sessionToken, isSubmitting, apiBaseUrl]
  );

  // Enviar informações adicionais para o vendedor
  const handleSendExtraInfo = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!extraInfo.trim() || !sessionToken || isSendingExtraInfo) return;

      setIsSendingExtraInfo(true);

      try {
        console.log('[WidgetSaaS] Enviando informações adicionais...');

        const response = await fetch(`${apiBaseUrl}/add-notes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_token: sessionToken,
            notes: extraInfo,
          }),
        });

        const data = (await response.json()) as { success: boolean; error?: string };

        if (data.success) {
          setShowExtraInfoForm(false);
          setExtraInfo('');

          // Mensagem de confirmação
          setMessages(prev => [
            ...prev,
            {
              id: `msg-${Date.now()}`,
              content: `Anotei tudo! 📝\n\nNosso vendedor vai receber essas informações extras. Obrigada!`,
              role: 'assistant',
            },
          ]);

          console.log('[WidgetSaaS] Informações adicionais enviadas com sucesso');
        } else {
          throw new Error(data.error || 'Failed to send notes');
        }
      } catch (error: any) {
        console.error('[WidgetSaaS] Send extra info error:', error);
        alert('Erro ao enviar informações. Tente novamente.');
      } finally {
        setIsSendingExtraInfo(false);
      }
    },
    [extraInfo, sessionToken, isSendingExtraInfo, apiBaseUrl]
  );

  // ========== EFFECTS ==========

  // Iniciar conversa ao abrir
  useEffect(() => {
    if (isOpen && !sessionToken && !isLoading && messages.length === 0) {
      startConversation();
    }
  }, [isOpen, sessionToken, isLoading, messages.length, startConversation]);

  // Auto-scroll para última mensagem (com delay para garantir que DOM renderizou)
  useEffect(() => {
    // Usar requestAnimationFrame para garantir que DOM foi atualizado
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, [messages]);

  // Focus no input quando abrir OU quando parar de carregar
  useEffect(() => {
    if (isOpen && !showForm && !submitSuccess && !isLoading) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, showForm, submitSuccess, isLoading]);

  // Esconder widget em páginas específicas
  useEffect(() => {
    const path = window.location.pathname;
    const hideOnPages = ['/login', '/register', '/chat', '/chatbot'];
    const shouldHide = hideOnPages.some(page => path.includes(page));

    if (shouldHide && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  // Cleanup: cancelar requests ao desmontar componente
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        console.log('[WidgetSaaS] Cleanup: cancelando requests em andamento');
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ========== HANDLERS ==========

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleSendMessage = useCallback(() => {
    if (inputMessage.trim()) {
      sendMessage(inputMessage);
    }
  }, [inputMessage, sendMessage]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleQuickAction = useCallback(
    (text: string) => {
      // CORREÇÃO: Enviar automaticamente ao invés de só preencher
      sendMessage(text);
    },
    [sendMessage]
  );

  // Renderizar botões de ação contextual baseado no estágio da conversa
  const renderQuickActions = useCallback(() => {
    if (isLoading || submitSuccess) return null;

    const lastAssistantMsg =
      [...messages]
        .reverse()
        .find(m => m.role === 'assistant')
        ?.content.toLowerCase() || '';

    // ESTÁGIO 1: Greeting - Pede nome + WhatsApp → SEM BOTÕES (texto livre)
    if (
      lastAssistantMsg.includes('qual seu nome') ||
      lastAssistantMsg.includes('whatsapp')
    ) {
      return null;
    }

    // ESTÁGIO 2: Qualificação - Pergunta o que trouxe → MOSTRAR BOTÕES DE PROBLEMAS
    if (
      lastAssistantMsg.includes('o que te trouxe') ||
      lastAssistantMsg.includes('como posso te ajudar')
    ) {
      return (
        <div className='flex gap-2 mt-3'>
          <Button
            variant='outline'
            size='sm'
            className='text-xs flex-1'
            onClick={() => handleQuickAction('Tenho muitos leads mas não consigo atender todos')}
            disabled={isLoading}
          >
            Muitos leads
          </Button>
          <Button
            variant='outline'
            size='sm'
            className='text-xs flex-1'
            onClick={() => handleQuickAction('Estou muito desorganizado, perco vendas')}
            disabled={isLoading}
          >
            Desorganizado
          </Button>
        </div>
      );
    }

    // Default: não mostrar botões
    return null;
  }, [messages, isLoading, submitSuccess, handleQuickAction]);

  // ========== RENDER ==========
  return (
    <>
      {/* Botão Flutuante */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            className={`fixed ${positionClasses[position]} z-[999]`}
          >
            <Button
              onClick={handleToggle}
              size='lg'
              className='rounded-full w-14 h-14 sm:w-16 sm:h-16 shadow-xl hover:shadow-2xl bg-white hover:bg-gray-50 border-2 border-gray-200 overflow-hidden p-0'
              aria-label='Abrir chat'
            >
              <img
                src='/organizador-clientes.avif'
                alt='Assistente ChatbotImóveis'
                className='w-full h-full object-cover'
              />
            </Button>

            {/* Badge de status da IA */}
            <span className='absolute -top-1 -right-1 flex h-3 w-3'>
              <span
                className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  aiStatus === 'online'
                    ? 'bg-green-400'
                    : aiStatus === 'checking'
                      ? 'bg-yellow-400'
                      : 'bg-red-400'
                }`}
              ></span>
              <span
                className={`relative inline-flex rounded-full h-3 w-3 ${
                  aiStatus === 'online'
                    ? 'bg-green-500'
                    : aiStatus === 'checking'
                      ? 'bg-yellow-500'
                      : 'bg-red-500'
                }`}
              ></span>
            </span>

            {/* Tooltip no desktop */}
            <div className='hidden sm:block absolute bottom-full mb-2 right-0 bg-black/80 text-white text-xs py-1 px-2 rounded whitespace-nowrap'>
              {aiStatus === 'online'
                ? 'Atendente Disponível'
                : aiStatus === 'checking'
                  ? 'Verificando conexão...'
                  : 'Atendente indisponível'}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Widget de Chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed ${positionClasses[position]} z-[999] w-[calc(100vw-32px)] sm:w-[380px]`}
          >
            <Card className='shadow-2xl border-0 flex flex-col bg-white dark:bg-gray-900 h-[450px] sm:h-[500px]'>
              {/* Header */}
              <div className='flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg'>
                <div className='flex items-center gap-3'>
                  <div className='w-10 h-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden'>
                    <img
                      src='/organizador-clientes.avif'
                      alt='Assistente'
                      className='w-full h-full object-cover'
                    />
                  </div>
                  <div>
                    <h3 className='font-semibold text-sm'>Assistente Virtual</h3>
                    <p className='text-xs opacity-90'>
                      {isLoading
                        ? 'Digitando...'
                        : aiStatus === 'online'
                          ? 'Online agora'
                          : aiStatus === 'checking'
                            ? 'Conectando...'
                            : 'Offline'}
                    </p>
                  </div>
                </div>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleToggle}
                  className='text-white hover:bg-white/20 p-1'
                  aria-label='Fechar'
                >
                  <X className='h-4 w-4' />
                </Button>
              </div>

              {/* Área de Mensagens */}
              <div className='flex-1 overflow-y-auto p-4 space-y-3'>
                {messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[75%] p-3 rounded-lg text-sm whitespace-pre-line ${
                        msg.role === 'assistant'
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                          : 'bg-blue-600 text-white'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                  <div className='flex justify-start'>
                    <div className='bg-gray-100 dark:bg-gray-800 p-3 rounded-lg'>
                      <Loader2 className='h-4 w-4 animate-spin text-gray-600' />
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Formulário de Cadastro */}
              {showForm && !submitSuccess ? (
                <div className='p-4 border-t bg-gray-50 dark:bg-gray-800'>
                  <form onSubmit={handleFinalize} className='space-y-3'>
                    <p className='text-xs text-gray-600 dark:text-gray-400 mb-2'>
                      Preencha seus dados para receber mais informações:
                    </p>
                    <div>
                      <Input
                        placeholder='Seu nome'
                        value={nome}
                        onChange={e => setNome(e.target.value)}
                        required
                        className='text-sm'
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <Input
                        placeholder='WhatsApp ou Email'
                        value={contato}
                        onChange={e => setContato(e.target.value)}
                        required
                        className='text-sm'
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className='flex gap-2'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setShowForm(false)}
                        className='flex-1'
                        disabled={isSubmitting}
                      >
                        Voltar
                      </Button>
                      <Button
                        type='submit'
                        size='sm'
                        className='flex-1 bg-blue-600 hover:bg-blue-700'
                        disabled={isSubmitting || !nome.trim() || !contato.trim()}
                      >
                        {isSubmitting ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Enviar'}
                      </Button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className='p-4 border-t'>
                  <div className='flex gap-2'>
                    <Input
                      ref={inputRef}
                      value={inputMessage}
                      onChange={e => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder='Digite sua mensagem...'
                      disabled={isLoading || submitSuccess}
                      className='flex-1'
                    />
                    <Button
                      onClick={handleSendMessage}
                      size='sm'
                      disabled={isLoading || !inputMessage.trim() || submitSuccess}
                      className='bg-blue-600 hover:bg-blue-700'
                      aria-label='Enviar'
                    >
                      <Send className='h-4 w-4' />
                    </Button>
                  </div>

                  {/* Ações rápidas contextuais */}
                  {!submitSuccess && renderQuickActions()}

                  {/* Botão de Cadastro */}
                  {!submitSuccess && messages.length > 2 && (
                    <Button
                      onClick={() => setShowForm(true)}
                      variant='outline'
                      size='sm'
                      className='w-full mt-3 text-xs border-blue-600 text-blue-600 hover:bg-blue-50'
                      disabled={isLoading}
                    >
                      Quero receber mais informações 📩
                    </Button>
                  )}

                  {/* Botão/Formulário de Informações Adicionais */}
                  {submitSuccess && !showExtraInfoForm && (
                    <Button
                      onClick={() => setShowExtraInfoForm(true)}
                      variant='outline'
                      size='sm'
                      className='w-full mt-3 text-xs border-green-600 text-green-600 hover:bg-green-50'
                    >
                      Enviar mais informações para o vendedor 📝
                    </Button>
                  )}

                  {submitSuccess && showExtraInfoForm && (
                    <form onSubmit={handleSendExtraInfo} className='mt-3 space-y-2'>
                      <p className='text-xs text-gray-600 dark:text-gray-400'>
                        Quer adicionar mais detalhes para o vendedor?
                      </p>
                      <textarea
                        value={extraInfo}
                        onChange={e => setExtraInfo(e.target.value)}
                        placeholder='Ex: Tenho urgência, trabalho com imóveis de alto padrão, etc.'
                        className='w-full p-2 text-sm border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500'
                        rows={3}
                        disabled={isSendingExtraInfo}
                      />
                      <div className='flex gap-2'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => {
                            setShowExtraInfoForm(false);
                            setExtraInfo('');
                          }}
                          className='flex-1'
                          disabled={isSendingExtraInfo}
                        >
                          Cancelar
                        </Button>
                        <Button
                          type='submit'
                          size='sm'
                          className='flex-1 bg-green-600 hover:bg-green-700'
                          disabled={isSendingExtraInfo || !extraInfo.trim()}
                        >
                          {isSendingExtraInfo ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            'Enviar'
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
